import { useState } from 'react'
import { Card, Table, Tag, Button, Space, Timeline, Badge } from 'antd'
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, MessageOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

const AnnotationReviewResults = () => {
  const navigate = useNavigate()

  const columns = [
    {
      title: '案件ID',
      dataIndex: 'caseId',
      key: 'caseId',
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
    },
    {
      title: '审核结果',
      dataIndex: 'result',
      key: 'result',
      render: (result: string) => {
        const colors: Record<string, string> = {
          approved: 'success',
          rejected: 'error',
          partial: 'warning',
        }
        const texts: Record<string, string> = {
          approved: '通过',
          rejected: '退回修改',
          partial: '部分通过',
        }
        const icons: Record<string, React.ReactNode> = {
          approved: <CheckCircleOutlined />,
          rejected: <CloseCircleOutlined />,
          partial: <MessageOutlined />,
        }
        return <Tag color={colors[result]} icon={icons[result]}>{texts[result]}</Tag>
      },
    },
    {
      title: '质量评分',
      dataIndex: 'score',
      key: 'score',
      render: (score: string) => {
        const config: Record<string, { stars: string; color: string }> = {
          excellent: { stars: '⭐⭐⭐⭐⭐ 优秀', color: 'green' },
          good: { stars: '⭐⭐⭐⭐ 良好', color: 'blue' },
          qualified: { stars: '⭐⭐⭐ 合格', color: 'default' },
          pending: { stars: '⭐⭐ 待改进', color: 'orange' },
        }
        const cfg = config[score]
        return <span style={{ color: cfg.color === 'green' ? '#52c41a' : cfg.color === 'blue' ? '#1890ff' : cfg.color === 'orange' ? '#fa8c16' : undefined }}>{cfg.stars}</span>
      },
    },
    {
      title: '审核人',
      dataIndex: 'reviewer',
      key: 'reviewer',
    },
    {
      title: '审核时间',
      dataIndex: 'reviewTime',
      key: 'reviewTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/annotation/${record.caseId}`)}>
            查看
          </Button>
          {record.result === 'rejected' && (
            <Button type="primary" onClick={() => navigate(`/annotation/${record.caseId}`)}>
              修改
            </Button>
          )}
        </Space>
      ),
    },
  ]

  const mockData = [
    {
      key: '1',
      caseId: 'CASE-2024-0155',
      projectName: '云南烟叶智慧消防建设项目',
      result: 'approved',
      score: 'excellent',
      reviewer: '审核员A',
      reviewTime: '2026-02-22 14:30',
    },
    {
      key: '2',
      caseId: 'CASE-2024-0153',
      projectName: '某医院医疗设备采购项目',
      result: 'rejected',
      score: 'pending',
      reviewer: '审核员B',
      reviewTime: '2026-02-22 11:20',
    },
    {
      key: '3',
      caseId: 'CASE-2024-0150',
      projectName: '某学校信息化建设项目',
      result: 'partial',
      score: 'good',
      reviewer: '审核员A',
      reviewTime: '2026-02-21 16:45',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">📋 我的审核结果</h1>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">45</div>
            <div className="text-gray-500 mt-1">审核通过</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">5</div>
            <div className="text-gray-500 mt-1">退回修改</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">8</div>
            <div className="text-gray-500 mt-1">部分通过</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">92%</div>
            <div className="text-gray-500 mt-1">平均通过率</div>
          </div>
        </Card>
      </div>

      <Card title="审核记录">
        <Table
          columns={columns}
          dataSource={mockData}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card title="🕐 最近审核动态">
        <Timeline>
          <Timeline.Item dot={<Badge status="success" />}>
            <div className="text-sm">
              <div className="font-medium">CASE-2024-0155 审核通过</div>
              <div className="text-gray-500">2026-02-22 14:30 by 审核员A</div>
              <div className="mt-1 text-gray-600">审核意见：标注质量优秀，数据完整准确</div>
            </div>
          </Timeline.Item>
          <Timeline.Item dot={<Badge status="error" />}>
            <div className="text-sm">
              <div className="font-medium">CASE-2024-0153 退回修改</div>
              <div className="text-gray-500">2026-02-22 11:20 by 审核员B</div>
              <div className="mt-1 text-gray-600">审核意见：答复理由需要补充法规依据</div>
            </div>
          </Timeline.Item>
          <Timeline.Item dot={<Badge status="warning" />}>
            <div className="text-sm">
              <div className="font-medium">CASE-2024-0150 部分通过</div>
              <div className="text-gray-500">2026-02-21 16:45 by 审核员A</div>
              <div className="mt-1 text-gray-600">审核意见：部分质疑点标注不完整</div>
            </div>
          </Timeline.Item>
        </Timeline>
      </Card>
    </div>
  )
}

export default AnnotationReviewResults
